// Every permission string in the app lives in this file. Values come from the
// backend permission catalogue (GET /public/v1/companies/{id}/permissions) —
// never invent one, and never inline a literal at a call site.
export type UserPermission =
  | INTERFACE_PERMISSIONS
  | COMPANY_PERMISSIONS
  | TIME_INTERFACE_PERMISSIONS
  | TEAMS_PERMISSIONS
  | PAY_PERMISSIONS
  | CONNECT_PERMISSIONS
  | MANAGEMENT_PERMISSIONS
  | BILLING_PERMISSIONS
  | GENERAL_PERMISSIONS;

export enum INTERFACE_PERMISSIONS {
  PAYROLL_OUTSOURCED = 'interface:can_access_payroll_outsourced',
  PAYROLL = 'interface:can_access_payroll',
  STAFF_MANAGEMENT = 'interface:can_access_management',
  CONNECT = 'interface:can_access_connect',
  TEAMS = 'interface:can_access_teams',
  BILLING = 'interface:can_access_billing',
  TIME = 'interface:can_access_time',
}

export enum PAY_PERMISSIONS {
  // Timesheets
  READ_TIMESHEET = 'pay:can_read_timesheet',
  CREATE_TIMESHEET = 'pay:can_create_timesheet',
  EDIT_TIMESHEET = 'pay:can_edit_timesheet',
  SUBMIT_TIMESHEET = 'pay:can_submit_timesheet',
  REOPEN_TIMESHEET = 'pay:can_reopen_timesheet',
  DELETE_TIMESHEET = 'pay:can_delete_timesheet',
  // Pay runs & payments
  CAN_READ_PAY_RUN = 'pay:can_read_pay_run',
  SUBMIT_PAY_RUN = 'pay:can_submit_pay_run',
  GENERATE_BACS = 'pay:can_generate_pay_run_bacs',
  CHECK_PAY_RUN_EMPLOYEES = 'pay:can_check_pay_run_employees',
  SEND_PAYSLIPS = 'pay:can_send_payslips',
  MANAGE_BANK_DETAILS = 'pay:can_manage_bank_details',
  // Pay schedules & employees
  READ_PAY_SCHEDULES = 'pay:can_read_pay_schedules',
  CREATE_PAY_SCHEDULES = 'pay:can_create_pay_schedules',
  EDIT_PAY_SCHEDULES = 'pay:can_edit_schedules',
  DELETE_PAY_SCHEDULES = 'pay:can_delete_pay_schedules',
  MANAGE_EMPLOYEES = 'pay:can_manage_employees',
  // HMRC
  MANAGE_HMRC_SETTINGS = 'pay:can_manage_hmrc_settings',
  READ_CIS300 = 'pay:can_read_cis300_report',
  SEND_CIS300 = 'pay:can_send_cis300_report',
}

export enum TIME_INTERFACE_PERMISSIONS {
  // Locations. `time:can_read_sites` is deprecated and merged into can_search_sites.
  LOCATIONS_AND_SHIFTS = 'time:can_search_sites',
  CREATE_SITES = 'time:can_create_sites',
  UPDATE_SITE = 'time:can_update_sites',
  REMOVE_SITES = 'time:can_remove_sites',
  // Shifts
  CREATE_SHIFTS = 'time:can_create_shifts',
  UPDATE_SHIFTS = 'time:can_update_shifts',
  REMOVE_SHIFTS = 'time:can_remove_shifts',
  // Location workers. USERS is the read right — it used to point at
  // can_update_workers, which gated reads behind a write permission.
  USERS = 'time:can_search_workers',
  ADD_WORKERS = 'time:can_add_workers',
  UPDATE_WORKERS = 'time:can_update_workers',
  REMOVE_WORKERS = 'time:can_remove_workers',
  /** PII-heavy: surfaces NI number, date of birth, phone and email. */
  LOOKUP_WORKERS = 'time:can_lookup_workers',
  SEARCH_WORKERS_SITES = 'time:can_search_workers_sites',
  READ_USER = 'time:can_read_user',
  // Time logs & approvals
  READ_TIME_LOGS = 'time:can_read_time_logs',
  APPROVE = 'time:can_approve_time_logs',
  MANUALLY_CLOCK_WORKERS = 'time:can_manually_clock_workers',
  // Live monitoring
  READ_ACTIVE_CHECK_IN = 'time:can_read_active_check_in',
  SEARCH_CHECKED_IN_WORKERS = 'time:can_search_checked_in_workers',
  READ_MAP_VIEW = 'time:can_read_map_view',
  READ_ACTIVITY_LOG = 'time:can_read_activity_log',
  // Module settings — this is what gates the Time Settings tab, not CREATE_SITES.
  MANAGE_TIME_SETTINGS = 'time:can_manage_time_settings',
}

export enum COMPANY_PERMISSIONS {
  CAN_READ = 'companies:can_read_company',
  CAN_EDIT = 'companies:can_edit_company',
  CAN_DELETE = 'companies:can_delete_company',
}

export enum TEAMS_PERMISSIONS {
  CAN_VIEW_TEAMS = 'teams:can_view_teams',
  CAN_CREATE_TEAMS = 'teams:can_create_teams',
  CAN_EDIT_TEAMS = 'teams:can_edit_teams',
  CAN_DELETE_TEAMS = 'teams:can_delete_teams',
  CAN_ADD_TEAM_MEMBERS = 'teams:can_add_team_members',
  CAN_REMOVE_TEAM_MEMBERS = 'teams:can_remove_team_members',
  CAN_CREATE_QUALIFICATION = 'teams:can_add_qualifications',
  /** Identity documents, Right-to-Work status, H&S documents. */
  CAN_VIEW_WORKER_DOCUMENTS = 'teams:can_view_worker_documents',
  /** "Request report" and the Reports page. Teams admins and managers by default. */
  CAN_GENERATE_REPORTS = 'teams:can_generate_reports',
  /** The Personal information section and the Full field set of an individual report. */
  CAN_EXPORT_PERSONAL_INFO = 'teams:can_export_personal_info',
}

export enum CONNECT_PERMISSIONS {
  READ_JOBS = 'connect:can_read_jobs',
  CREATE_JOBS = 'connect:can_create_jobs',
  EDIT_JOBS = 'connect:can_edit_jobs',
  READ_JOB_CANDIDATES = 'connect:can_read_job_candidates',
  APPLY_WORKERS = 'connect:can_apply_workers',
  ACCEPT_WORKERS = 'connect:can_accept_workers',
  DECLINE_WORKERS = 'connect:can_decline_workers',
  CANCEL_WORKERS = 'connect:can_cancel_workers',
  REVERT_WORKERS = 'connect:can_revert_workers',
}

export enum MANAGEMENT_PERMISSIONS {
  LIST_STAFF = 'management:can_list_staff',
  READ_ROLES = 'management:can_read_roles',
  READ_PERMISSIONS = 'management:can_read_permissions',
  MANAGE_STAFF_ROLES = 'management:can_manage_staff_roles',
  INVITE_WORKERS = 'management:can_invite_workers',
  /** Company Manager's right; covers inviting workers anywhere, like INVITE_WORKERS. */
  MANAGE_WORKER_ACCESS = 'management:can_manage_worker_access',
  MANAGE_WORKER_ACCOUNTS = 'management:can_manage_worker_accounts',
  MANAGE_SCHEDULED_NOTIFICATIONS = 'management:can_manage_scheduled_notifications',
  /** Worker profile preview, including Passport and Right to Work documents. */
  VIEW_WORKER_PROFILES = 'management:can_view_worker_profiles',
  /**
   * Company-wide worker visibility. Not a real row in ath_permissions — the
   * backend writes it straight into res_resources.actions. Unused so far;
   * confirm which array it lands in before gating anything on it.
   */
  VIEW_ALL_WORKERS = 'management:can_view_all_workers',
}

/**
 * Either one lets the user invite anyone in the company into any team, location or pay
 * schedule (the invite dialog, its pickers, Retry). No module right is checked on top —
 * don't hide target types by module rights.
 */
export const INVITE_WORKERS_PERMISSIONS: UserPermission[] = [
  MANAGEMENT_PERMISSIONS.INVITE_WORKERS,
  MANAGEMENT_PERMISSIONS.MANAGE_WORKER_ACCESS,
];

export enum BILLING_PERMISSIONS {
  VIEW_BILLING = 'billing:can_view_billing',
  EDIT_BILLING = 'billing:can_edit_billing',
}

export enum GENERAL_PERMISSIONS {
  VIEW_DASHBOARDS = 'general:can_view_dashboards',
}

export enum MODULES {
  PAYROLL = 'Payroll',
  COMPANY = 'Company',
  CONNECT = 'Connect',
  TEAMS = 'Teams',
  TIME = 'Time&Attendance',
}

export enum INVITE_MODULES {
  TIME = 'time',
  PAY = 'pay',
  TEAMS = 'teams',
  GENERAL = 'general',
}

export interface UserModules {
  pay: boolean;
  teams: boolean;
  time: boolean;
}

export interface InviteEmployeePayload {
  pay?: Array<{ pay_schedule_id?: string; pay_scheme?: string }>;
  teams?: Array<{ team_id?: string }>;
  time?: Array<{ site_id?: string; shift_id?: string }>;
}

export interface ActiveEmployeeInvite {
  uuid: string;
  email?: string;
  phone?: string;
  company_id: string;
  company_name?: string;
  status: string;
  employee_payload: InviteEmployeePayload;
  token: string;
  updated_at?: string;
  created_at?: string;
}

export const deriveInviteModules = (invites: ActiveEmployeeInvite[]): UserModules => ({
  pay: invites.some(i => !!i.employee_payload?.pay?.length),
  teams: invites.some(i => !!i.employee_payload?.teams?.length),
  time: invites.some(i => !!i.employee_payload?.time?.length),
});

export interface Module {
  uuid: string;
  name: string;
  system_name: string;
  description: string;
}

export interface Permission {
  uuid: string;
  system_name: string;
  name: string;
  actions_group: string;
  description: string;
  visible: boolean;
}

export interface Role {
  uuid: string;
  name: string;
  description: string;
  module: Module;
  permissions: Permission[];
}
